import { IonItem, IonLabel, IonList, IonAvatar, IonBadge } from '@ionic/react';
import LoadingSpinner from './LoadingSpinner';
import { Box, Typography, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { collection, query, where, onSnapshot, orderBy, getDoc, doc, Timestamp, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Company, Contractor, Ambassador } from '../types/user';
import { useAuth } from '../hooks/useAuth';
import { Conversation } from '../types/message';
import { BaseUser } from '../types/user';

interface MessageListProps {
  onSelectConversation: (conversationId: string, otherUser: BaseUser) => void;
}

export const MessageList: React.FC<MessageListProps> = ({ onSelectConversation }) => {
  const { t } = useTranslation();
  const { user, userData } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [otherUsers, setOtherUsers] = useState<Record<string, BaseUser>>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user?.uid) return;

    // Temporary query without orderBy while index is building
    const q = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', user.uid)
    );

    setIsLoading(true);
    const unsubscribe = onSnapshot(q, async (snapshot) => {
      try {
        const conversationsData: Conversation[] = [];
        const usersToFetch = new Set<string>();

        snapshot.forEach((doc) => {
          const data = doc.data();
          const conversation = {
            ...data,
            id: doc.id,
          } as Conversation;
          conversationsData.push(conversation);

          // Collect other participant IDs to fetch their user data
          conversation.participants
            .filter(id => id !== user.uid)
            .forEach(id => usersToFetch.add(id));
        });

        console.log('Conversations loaded:', conversationsData);

        // Fetch user data for all other participants
        const userPromises = Array.from(usersToFetch).map(async (userId) => {
          const userDoc = await getDoc(doc(db, 'users', userId));
          const userData = userDoc.data() as Company | Contractor | Ambassador;
          return { ...userData, id: userId };
        });

        const users = await Promise.all(userPromises);
        const usersMap = users.reduce((acc, user) => {
          acc[user.id] = user;
          return acc;
        }, {} as Record<string, BaseUser>);

        // Sort conversations by updatedAt on the client side
        const sortedConversations = conversationsData.sort((a, b) => 
          b.updatedAt.toMillis() - a.updatedAt.toMillis()
        );
        
        setOtherUsers(usersMap);
        setConversations(sortedConversations);
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading conversations:', error);
      }
    });

    return () => unsubscribe();
  }, [user?.uid]);

  const getOtherParticipant = (conversation: Conversation): BaseUser | undefined => {
    const otherUserId = conversation.participants.find(id => id !== user?.uid);
    return otherUserId ? otherUsers[otherUserId] : undefined;
  };

  const formatTimestamp = (timestamp: Timestamp) => {
    const now = new Date();
    const date = timestamp.toDate();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      return date.toLocaleDateString([], { weekday: 'long' });
    } else {
      return date.toLocaleDateString();
    }
  };

  const history = useHistory();

  const handleSelectConversation = async (conversationId: string, otherUser: BaseUser) => {
    // Reset unread count when opening the conversation
    if (user?.uid) {
      const conversationRef = doc(db, 'conversations', conversationId);
      await updateDoc(conversationRef, {
        [`unreadCount.${user.uid}`]: 0
      });
    }
    // Update URL with conversation parameter
    history.replace(`/messages?conversation=${conversationId}`);
    onSelectConversation(conversationId, otherUser);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (conversations.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          {t('messages.no_conversations')}
        </Typography>
      </Box>
    );
  }

  return (
    <IonList>
      {conversations.map((conversation) => {
        const otherUser = getOtherParticipant(conversation);
        if (!otherUser) return null;

        const unreadCount = conversation.unreadCount?.[user?.uid || ''] || 0;

        return (
          <IonItem
            key={conversation.id}
            button
            onClick={() => handleSelectConversation(conversation.id, otherUser)}
            className="conversation-item"
          >
            <IonAvatar slot="start" className="small-avatar">
              <img
                src={otherUser.profilePhotoUrl || '/assets/image/logo.png'}
                alt={`${otherUser.type === 'company' 
                  ? (otherUser as Company).companyName 
                  : `${(otherUser as Contractor | Ambassador).firstName} ${(otherUser as Contractor | Ambassador).lastName}`}'s avatar`}
              />
            </IonAvatar>
            <IonLabel className="compact-label">
              <h2 className="truncate-text">
                {otherUser.type === 'company'
                  ? (otherUser as Company).companyName
                  : `${(otherUser as Contractor | Ambassador).firstName} ${(otherUser as Contractor | Ambassador).lastName}`}
              </h2>
              {conversation.lastMessage && (
                <div className="message-preview">
                  <p className="truncate-text">{conversation.lastMessage.content}</p>
                </div>
              )}
            </IonLabel>
            {unreadCount > 0 && (
              <IonBadge slot="end" color="primary" className="unread-badge">
                {unreadCount}
              </IonBadge>
            )}
          </IonItem>
        );
      })}
    </IonList>
  );
};
